/**
 * ReportMessageModal Component
 * Modal for reporting a message with severity selection and reason text.
 */

import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useI18n } from '../../context/I18nContext';
import './ReportMessageModal.css';

const ReportMessageModal = ({ eventId, isEncrypted, onClose, onSubmit }) => {
  const { t } = useI18n();
  const [score, setScore] = useState(-50); // Default: Offensive
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const severityOptions = [
    { value: -100, label: t('ReportMessage.extremelyOffensive', 'Extremely offensive') },
    { value: -50, label: t('ReportMessage.offensive', 'Offensive') },
    { value: 0, label: t('ReportMessage.inoffensive', 'Inoffensive') },
  ];

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);

    // Capture previous overflow value before modifying
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      // Restore previous value
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  // Handle overlay click to close
  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    if (!reason.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onSubmit(eventId, score, reason.trim());
    } catch {
      // Defensive: catch unexpected errors; normal errors returned via { success: false }
    } finally {
      setIsSubmitting(false);
    }
  }, [eventId, score, reason, isSubmitting, onSubmit]);

  const isSubmitDisabled = !reason.trim() || isSubmitting;

  return createPortal(
    <div className="report-modal-overlay" onClick={handleOverlayClick}>
      <div className="report-modal">
        {/* Header */}
        <div className="report-modal-header">
          <h2 className="report-modal-title">
            {t('ReportMessage.title', 'Report Message')}
          </h2>
          <button
            className="report-modal-close"
            onClick={onClose}
            type="button"
            aria-label={t('ReportMessage.close', 'Close')}
          >
            <ion-icon name="close-outline" />
          </button>
        </div>

        {/* Body */}
        <div className="report-modal-body">
          {/* Severity Selection */}
          <div className="report-modal-field">
            <label htmlFor="report-severity" className="report-modal-label">
              {t('ReportMessage.howOffensive', 'How offensive is this message?')}
            </label>
            <select
              id="report-severity"
              className="report-modal-select"
              value={score}
              onChange={(e) => setScore(Number(e.target.value))}
            >
              {severityOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Reason Textarea */}
          <div className="report-modal-field">
            <label htmlFor="report-reason" className="report-modal-label">
              {t('ReportMessage.whyReport', 'Why are you reporting this message?')}
            </label>
            <textarea
              id="report-reason"
              className="report-modal-textarea"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('ReportMessage.reasonPlaceholder', 'Describe the reason for reporting...')}
              rows={4}
            />
          </div>

          {/* Encryption Warning */}
          {isEncrypted && (
            <p className="report-modal-encryption-warning">
              {t('ReportMessage.encryptionWarning', 'This room is encrypted. The server administrator may not be able to read the reported message.')}
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="report-modal-actions">
          <button
            className="report-modal-btn report-modal-btn-cancel"
            onClick={onClose}
            type="button"
            disabled={isSubmitting}
          >
            {t('Modal.cancel', 'Cancel')}
          </button>
          <button
            className="report-modal-btn report-modal-btn-submit"
            onClick={handleSubmit}
            type="button"
            disabled={isSubmitDisabled}
          >
            {isSubmitting
              ? t('ReportMessage.submitting', 'Submitting...')
              : t('ReportMessage.submit', 'Submit Report')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

ReportMessageModal.propTypes = {
  eventId: PropTypes.string.isRequired,
  isEncrypted: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default ReportMessageModal;
